"use client";

import { useEffect } from "react";
import { baloo, inter, pacifico } from "./fonts";
// global-error REPLACES the root layout (app/layout.tsx) when it fires, so none
// of that layout's assets — including this stylesheet — ship with it. Import it
// directly or this page renders completely unstyled.
import "./globals.css";

// app/layout.tsx puts these three `.variable` classNames on <html>, which is
// what makes globals.css's --font-logo / --font-display / --font-ui tokens
// resolve to Pacifico, Baloo 2 and Inter instead of falling back to system-ui.
// This file renders its own <html>, so it hangs all three on it again (the
// faces are declared once, in ./fonts), or the wordmark and heading silently
// render in the wrong typeface while the rest of the app looks right.

/**
 * Root-layout error boundary. Fires when app/layout.tsx itself throws, or when
 * a throw elsewhere has no error.tsx boundary above it to land in. It fully
 * replaces the root layout, so it must render its own <html>/<body>.
 *
 * Styled from the app's own tokens via the `.auth-page` / `.auth-card` idiom —
 * the one other full-screen standalone surface GiftMe has.
 */
export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  useEffect(() => {
    void (async () => {
      try {
        const { reportError } = await import("@/lib/flare");
        await reportError(error, { kind: "client", digest: error.digest });
      } catch {
        // The page is already broken; a failed report must not make it worse.
      }
    })();
  }, [error]);

  return (
    <html lang="en" className={`${baloo.variable} ${inter.variable} ${pacifico.variable}`}>
      <head>
        {/* Next's global-error path bypasses metadata/viewport resolution
            entirely (its own DefaultGlobalError has the same gap), so without
            this the installed iOS PWA renders this page at desktop width —
            exactly the scenario this file exists for. */}
        <meta name="viewport" content="width=device-width, initial-scale=1, viewport-fit=cover" />
      </head>
      <body>
        <main className="auth-page">
          <div className="auth-card">
            <div className="auth-brand">
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img src="/mark.svg" alt="" aria-hidden="true" width={44} height={44} />
              <span className="word">Gift<b>Me</b></span>
            </div>
            <h1 className="auth-title">GiftMe ran into a problem</h1>
            <p className="auth-sub">
              Something went wrong while this page was loading. Trying again usually brings it back.
            </p>
            <div className="auth-form">
              <button
                type="button"
                className="btn btn-primary auth-submit"
                onClick={() => reset()}
                style={{ minHeight: "44px" }}
              >
                Try again
              </button>
            </div>
          </div>
        </main>
      </body>
    </html>
  );
}
